import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { convertMathJax, getMasterLabel, shuffleArray, typesetMath } from "./AssessmentApp";
import { QuestionRenderer } from "modules/questions/QuestionRenderer";

/**
 * The main assessment component, now supporting two modes:
 * 1. Standard Assessment (isLiveQuiz=false): Multi-question, timed, final submit.
 * 2. Live Quiz (isLiveQuiz=true): Single-question, instant submit via callback, no internal navigation.
 * * @param {object} props - The component props.
 * @param {Array<object>} props.questionsData - The list of questions (full list or single active question).
 * @param {number} [props.timerDuration=2] - Global timer duration in minutes (ignored if isLiveQuiz).
 * @param {Function} props.onExit - Callback to exit the assessment.
 * @param {Function} props.onAssessmentSubmit - Callback to submit the assessment (final or instant answer).
 * @param {boolean} [props.isSubmitting=false] - Indicates if submission is in progress.
 * @param {string} props.submitError - The error message if submission failed.
 * @param {object} props.course - Course metadata.
 * @param {object} props.evaluation - Evaluation metadata.
 * @param {boolean} [props.enableTimer=true] - Enables the global timer display/logic.
 * @param {boolean} [props.isLiveQuiz=false] - If true, enables single-question mode.
 * @param {boolean} [props.readOnly=false] - If true, disables all inputs (Tutor view or student answered).
 * @param {string} [props.initialAnswer=null] - Pre-existing answer for the current question in Live Quiz mode.
 */
export const Assessment = ({
    questionsData, timerDuration = 2, onExit, onAssessmentSubmit, isSubmitting, submitError, course, evaluation, enableTimer = true,
    isLiveQuiz = false, readOnly = false, initialAnswer = null
}) => {
    // In Live Quiz mode, questionsData is already the active question [q1], 
    // so we skip shuffling and keep it as a constant single item array.
    const initialQuestions = useMemo(() => {
        if (isLiveQuiz) return questionsData.slice(0, 1);
        return shuffleArray(questionsData);
    }, [questionsData, isLiveQuiz]);

    const [questions, setQuestions] = useState(initialQuestions);
    const [currentIndex, setCurrentIndex] = useState(0);
    // If Live Quiz, initialize answers with the provided initialAnswer
    const [answers, setAnswers] = useState(() => {
        if (isLiveQuiz && initialQuestions.length > 0 && initialAnswer) {
            return { [initialQuestions[0].id]: initialAnswer };
        }
        return {};
    });
    const [submitted, setSubmitted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(timerDuration * 60);
    const [showHint, setShowHint] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const optionsRef = useRef(null);
    const [questionMetricsMap, setQuestionMetricsMap] = useState({});
    const questionStartRef = useRef(Date.now());

    const isFinished = submitted || (enableTimer && timeLeft === 0);
    const currentQuestion = questions[currentIndex];

    // Determine input disability based on mode and props
    const inputDisabled = submitted || timeLeft === 0 || isSubmitting || readOnly;


    const subjectLabel = useMemo(
        () => getMasterLabel("subjects", course?.subject),
        [course?.subject]
    );

    useEffect(() => {
        typesetMath(optionsRef.current);
    }, [currentIndex, isLiveQuiz]);

    // --- Global Timer Logic (Disabled for Live Quiz Mode) ---
    useEffect(() => {
        // Only run global timer logic if NOT in Live Quiz mode AND timer is enabled
        if (isLiveQuiz || !enableTimer) { return }

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timer);
                    if (!submitted && !isSubmitting) {
                        handleSubmit();
                    }
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [submitted, isSubmitting, isLiveQuiz, enableTimer]);

    // --- Navigation Handlers (Disabled for Live Quiz Mode) ---
    const recordTimeSpent = useCallback(() => {
        try {
            const prevQuestionId = questions[currentIndex]?.id;
            if (prevQuestionId) {
                const deltaSeconds = Math.max(
                    0,
                    Math.round((Date.now() - questionStartRef.current) / 1000)
                );
                setQuestionMetricsMap((prev) => ({
                    ...prev,
                    [prevQuestionId]: {
                        ...(prev[prevQuestionId] || {}),
                        timeSpentSeconds: (prev[prevQuestionId]?.timeSpentSeconds || 0) + deltaSeconds,
                    },
                }));
            }
        } catch { }
    }, [currentIndex, questions]);


    const nextQuestion = useCallback(() => {
        if (isLiveQuiz) return;
        recordTimeSpent();
        setCurrentIndex((prevIndex) => prevIndex < questions.length - 1 ? prevIndex + 1 : prevIndex);
        setShowHint(false);
        questionStartRef.current = Date.now();
    }, [isLiveQuiz, questions, recordTimeSpent]);

    const prevQuestion = () => {
        if (isLiveQuiz) return;
        if (currentIndex > 0) {
            recordTimeSpent();
            setCurrentIndex((prev) => prev - 1);
            setShowHint(false);
            questionStartRef.current = Date.now();
        }
    };
    // ----------------------------------------------------

    const getCorrectHint = (question) => {
        const correctChoice = question?.answerChoices?.find(
            (choice) => choice.isCorrect
        );
        return correctChoice?.hint || null;
    };

    // --- Answer Selection Handler ---
    const handleSelect = (option, incrementAttempts = true) => {
        if (inputDisabled) return;

        // 1. Update local state
        setAnswers((prev) => ({
            ...prev,
            [currentQuestion.id]: option,
        }));

        // 2. Track metrics (only in standard assessment)
        if (!isLiveQuiz && incrementAttempts) {
            // ... existing metric tracking logic for attempts/time
        }

        // 3. CRITICAL: Instant submission for Live Quiz
        if (isLiveQuiz) {
            // In Live Quiz mode, handleSelect IS the submission event
            const liveResult = [{
                id: currentQuestion.id,
                answer: option,
                isCorrect: currentQuestion.answerChoices.find(c => c.choiceText === option)?.isCorrect || false
            }];
            // We pass a single item array result to the parent wrapper
            onAssessmentSubmit(liveResult);
        }
    };

    // --- Final Assessment Submission Handler (Standard Mode Only) ---
    const handleSubmit = () => {
        if (isLiveQuiz) return; // Prevent global submit in Live Quiz mode

        // 1. Record final time for the last question
        recordTimeSpent();

        // 2. Calculate final results and metrics (original logic)
        let finalScore = 0;
        questions.forEach((question) => {
            const userAnswer = answers[question.id];
            const correctAnswer = question.answerChoices?.find(
                (choice) => choice.isCorrect
            )?.choiceText;
            if (userAnswer && correctAnswer && userAnswer === correctAnswer) {
                finalScore++;
            }
        });

        // compute final delta for the current question (avoid relying on async state)
        let finalQuestionId = null;
        let finalDeltaSeconds = 0;
        try {
            finalQuestionId = questions[currentIndex]?.id ?? null;
            if (finalQuestionId) {
                finalDeltaSeconds = Math.max(
                    0,
                    Math.round((Date.now() - questionStartRef.current) / 1000)
                );
            }
        } catch { /* ignore */ }

        // build question-level metrics (simplified)
        const questionMetrics = questions.map((q) => {
            const m = questionMetricsMap[q.id] || {};
            const selectedAnswer = answers[q.id];
            const correctChoice = q.answerChoices?.find(
                (c) => c.isCorrect
            )?.choiceText;
            const timeForThisQuestion = (m.timeSpentSeconds || 0) +
                (q.id === finalQuestionId ? finalDeltaSeconds : 0);
            return {
                questionCode: String(q.code || q.questionCode || q.id),
                questionType: q.questionType,
                isCorrect: selectedAnswer != null && correctChoice != null
                    ? selectedAnswer === correctChoice
                    : false,
                timeSpentSeconds: timeForThisQuestion,
                attempts: m.attempts || 0,
                selectedAnswer: selectedAnswer !== undefined && selectedAnswer !== null
                    ? String(selectedAnswer)
                    : null,
                correctAnswer: correctChoice !== undefined && correctChoice !== null
                    ? String(correctChoice)
                    : null,
                skipped: selectedAnswer == null,
            };
        });


        const timeTakenSeconds = timerDuration * 60 - timeLeft;

        // Pass calculated data to the parent component's callback
        onAssessmentSubmit(
            finalScore,
            answers,
            questions,
            questionMetrics,
            timeTakenSeconds
        );
        setSubmitted(true);
    };

    const toggleHint = () => {
        setShowHint((prev) => !prev);
    };

    const ExitConfirmationModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
            <div className="relative p-8 bg-white dark:bg-gray-900 w-full max-w-sm rounded-lg border border-gray-300 dark:border-gray-700 animate-fade-in-down">
                <h3 className="text-xl font-bold text-black dark:text-white mb-4">
                    Confirm Exit
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                    Are you sure you want to exit the assessment? Your progress will be
                    lost.
                </p>
                <div className="flex justify-end space-x-4">
                    <button
                        onClick={() => setShowExitModal(false)}
                        className="px-6 py-2 bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white rounded-full font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            setShowExitModal(false);
                            onExit();
                        }}
                        className="px-6 py-2 bg-red-500 text-white rounded-full font-semibold hover:bg-red-600 transition-colors duration-200"
                    >
                        Exit
                    </button>
                </div>
            </div>
        </div>
    );

    if (!currentQuestion) return <div className="p-4 text-center text-gray-500">No active question.</div>;

    return (
        <div className={`container mx-auto p-4 md:p-8 flex flex-col ${isLiveQuiz ? 'w-full' : 'lg:flex-row gap-8 w-full min-h-screen'} bg-transparent`}>
            {showExitModal && <ExitConfirmationModal />}

            {/* Sidebar / Info Block (Hidden for Live Quiz Student View, always shown for Standard Assessment) */}
            {/* The entire sidebar is hidden if isLiveQuiz is true */}
            {!isLiveQuiz && (
                <div className="w-full lg:w-1/3 flex flex-col gap-6 order-1 lg:order-2">
                    {enableTimer &&
                        <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-800 text-center bg-white dark:bg-gray-900 shadow-md">
                            <p className="text-xl font-bold text-black dark:text-white">Time Left:</p>
                            <p className="text-5xl font-extrabold text-blue-600 dark:text-blue-400 mt-2">
                                {String(Math.floor(timeLeft / 60)).padStart(2, "0")} :{" "}
                                {String(timeLeft % 60).padStart(2, "0")}
                            </p>
                        </div>
                    }
                    {course?.courseName && subjectLabel && <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md">
                        <p className="text-lg font-bold text-black dark:text-white">
                            Course: <span className="text-blue-600 dark:text-blue-400">{course?.courseName || "-"}</span>
                        </p>
                        <p className="text-lg font-bold text-black dark:text-white mt-2">
                            Subject: <span className="text-blue-600 dark:text-blue-400">{subjectLabel || "-"}</span>
                        </p>
                    </div>}

                    <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md">
                        <p className="text-lg font-bold text-black dark:text-white">
                            Question <span className="text-blue-600 dark:text-blue-400">{currentIndex + 1}</span> / {questions.length}
                        </p>
                        <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-3 mt-4">
                            <div
                                className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-out"
                                style={{
                                    width: `${((currentIndex + 1) / questions.length) * 100}%`,
                                }}
                            ></div>
                        </div>
                    </div>

                    {getCorrectHint(currentQuestion) && (
                        <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md">
                            <button
                                className="w-full px-6 py-3 bg-purple-500 dark:bg-purple-600 text-white font-bold rounded-full transition-colors duration-200"
                                onClick={toggleHint}
                            >
                                {showHint ? "Hide Hint" : "Show Hint"}
                            </button>
                            {showHint && (
                                <p className="mt-4 p-4 bg-purple-100 dark:bg-purple-900 border-l-4 border-purple-400 dark:border-purple-600 text-purple-800 dark:text-purple-200 rounded-lg animate-fade-in">
                                    {getCorrectHint(currentQuestion)}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Main content container */}
            <div className="flex-grow flex flex-col gap-6 order-2 lg:order-1">
                {/* Hide the Assessment title bar in Live Quiz mode if not needed */}
                {!isLiveQuiz && <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md">
                    <h1 className="text-3xl font-bold text-center text-black dark:text-white tracking-tight">
                        {evaluation?.name || "Assessment"}
                    </h1>
                </div>}

                <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md">

                    {/* Render the current question */}
                    <QuestionRenderer
                        question={currentQuestion}
                        index={currentIndex} />

                    {!(currentQuestion.questionType === "OPEN_ENDED"
                        || currentQuestion.questionType === "FILL_IN_THE_BLANK"
                        || currentQuestion.questionType === "MATHS_CALCULATION") && (
                            <div ref={optionsRef}>
                                {currentQuestion?.answerChoices
                                    ?.slice()
                                    .sort((a, b) => a.id - b.id)
                                    .map((choice) => (
                                        <label
                                            key={choice.id}
                                            className={`
                                flex items-center p-4 my-2 border-2 rounded-xl cursor-pointer transition-all duration-200
                                ${answers[currentQuestion.id] === choice.choiceText
                                                    ? "bg-blue-100 dark:bg-blue-900 border-blue-500 dark:border-blue-400 ring-2 ring-blue-300"
                                                    : "bg-transparent border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"}
                            `}
                                        >
                                            <input
                                                type="radio"
                                                name={currentQuestion.id}
                                                value={choice.choiceText}
                                                onChange={() => handleSelect(choice.choiceText)}
                                                disabled={inputDisabled} // Use unified disabled state
                                                checked={answers[currentQuestion.id] === choice.choiceText}
                                                className="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400 focus:ring-blue-500 accent-blue-500 dark:accent-blue-400" />
                                            <span className="text-black dark:text-white font-medium">
                                                {convertMathJax(choice.choiceText)}
                                            </span>
                                        </label>
                                    ))}
                            </div>
                        )}
                    {/* Open-Ended/Fill-in-the-Blank */}

                    {(questions[currentIndex].questionType === "OPEN_ENDED"
                        || questions[currentIndex].questionType === "FILL_IN_THE_BLANK"
                        || questions[currentIndex].questionType === "MATHS_CALCULATION") && (
                            <div key={`open-ended-textarea-${currentIndex}`}>
                                <textarea
                                    value={answers[questions[currentIndex].id] || ""}
                                    onChange={(e) => handleSelect(e.target.value, false)}
                                    onBlur={(e) => handleSelect(e.target.value, true)}
                                    placeholder="Your answer..."
                                    disabled={submitted || timeLeft === 0 || isSubmitting}
                                    className="w-full h-32 p-3 mt-4 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 text-black dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed" />
                            </div>
                        )}

                    {submitError && (
                        <p className="mt-4 text-center text-red-500 dark:text-red-400 font-medium">
                            {submitError}
                        </p>
                    )}

                    {/* --- Navigation Buttons (Hidden for Live Quiz Mode) --- */}
                    {/* This entire section is hidden if isLiveQuiz is true, delegating control to the wrapper */}
                    {!isLiveQuiz && (
                        <div className="flex justify-between items-center mt-8 gap-4">
                            <button
                                className="px-6 py-3 bg-red-500 text-white font-bold rounded-full transition-colors duration-200 flex-grow"
                                onClick={() => setShowExitModal(true)}
                            >
                                Exit
                            </button>
                            <button
                                className="px-6 py-3 bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white font-bold rounded-full transition-colors duration-200 flex-grow hover:bg-gray-100 dark:hover:bg-gray-800"
                                onClick={prevQuestion}
                                disabled={currentIndex === 0 || isSubmitting}
                            >
                                Previous
                            </button>
                            {currentIndex < questions.length - 1 ? (
                                <button
                                    className="px-6 py-3 bg-blue-500 text-white font-bold rounded-full transition-colors duration-200 flex-grow hover:bg-blue-600"
                                    onClick={nextQuestion}
                                    disabled={isSubmitting}
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    className={`px-6 py-3 text-white font-bold rounded-full transition-colors duration-200 flex-grow ${isSubmitting || isFinished
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-500 hover:bg-green-600"}`}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || isFinished}
                                >
                                    {isSubmitting ? "Submitting..." : (isFinished ? "Submitted" : "Submit")}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};